import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import App from './App'
import './index.css'
// Self-hosted fonts — avoids cross-origin requests that Cloudflare's
// signature-agent header causes to fail CORS preflight on fonts.gstatic.com.
import '@fontsource/space-grotesk/latin-500.css'
import '@fontsource/space-grotesk/latin-600.css'
import '@fontsource/space-grotesk/latin-700.css'
import '@fontsource/dm-sans/latin-400.css'
import '@fontsource/dm-sans/latin-500.css'
import '@fontsource/jetbrains-mono/latin-400.css'

// Surface unhandled async failures — without these, a promise that rejects
// without a .catch() (or a throw inside a setTimeout/event handler) disappears
// silently in production. We log to the console so dev tools still show them
// and users opening DevTools can report issues with real stack traces.
if (typeof window !== 'undefined') {
    window.addEventListener('unhandledrejection', (event) => {
        console.error('Unhandled promise rejection:', event.reason);
    });
    window.addEventListener('error', (event) => {
        console.error('Unhandled error:', event.error || event.message);
    });
}

// WebMCP — expose PostPaid's key actions to in-browser AI agents.
// Runs once on page load; the AbortController lets the browser clean up if
// the module is ever unloaded (e.g. during hot-module replacement in dev).
if (typeof navigator !== 'undefined' && navigator.modelContext?.registerTool) {
    const controller = new AbortController();

    navigator.modelContext.registerTool(
        {
            name: 'search_campaigns',
            description: 'Browse active PostPaid influencer marketing campaigns. Returns a list of live campaigns a creator can apply to.',
            inputSchema: {
                type: 'object',
                properties: {
                    query: { type: 'string', description: 'Optional keyword to filter campaigns by name or description' },
                },
                required: [],
            },
            execute: async ({ query } = {}) => {
                const params = query ? `?q=${encodeURIComponent(query)}` : '';
                const res = await fetch(`/api/campaigns${params}`, { credentials: 'include' });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return res.json();
            },
        },
        { signal: controller.signal },
    );

    navigator.modelContext.registerTool(
        {
            name: 'get_guides',
            description: "List PostPaid's educational guides on influencer marketing strategy, CPE benchmarks, and micro-influencer tactics.",
            inputSchema: { type: 'object', properties: {}, required: [] },
            execute: async () => {
                return [
                    { title: 'Pay-Per-Interaction Influencer Marketing', url: '/guides/pay-per-interaction-influencer-marketing' },
                    { title: 'Micro-Influencer Marketing', url: '/guides/micro-influencer-marketing' },
                    { title: 'CPE Benchmarks 2026', url: '/guides/cpe-benchmarks-2026' },
                ];
            },
        },
        { signal: controller.signal },
    );

    navigator.modelContext.registerTool(
        {
            name: 'get_tools',
            description: "List PostPaid's free influencer marketing tools (CPE calculators, benchmark lookups, etc.).",
            inputSchema: { type: 'object', properties: {}, required: [] },
            execute: async () => {
                const res = await fetch('/api/tools', { credentials: 'include' });
                if (!res.ok) {
                    // Fall back to the tools page link if the endpoint isn't available
                    return [{ title: 'PostPaid Tools', url: '/tools' }];
                }
                return res.json();
            },
        },
        { signal: controller.signal },
    );

    navigator.modelContext.registerTool(
        {
            name: 'get_site_info',
            description: 'Return general information about PostPaid: what it is, who it is for, and how to get started.',
            inputSchema: { type: 'object', properties: {}, required: [] },
            execute: async () => ({
                name: 'PostPaid',
                description: 'A performance-based influencer marketing platform. Brands pay creators only for measurable engagements (likes, comments, saves) — no flat fees.',
                for: ['brands launching influencer campaigns', 'creators monetising their audience'],
                getStarted: {
                    brands: '/register/business',
                    creators: '/register/influencer',
                },
                contact: '/contact',
            }),
        },
        { signal: controller.signal },
    );
}

ReactDOM.createRoot(document.getElementById('root')).render(
    <React.StrictMode>
        <BrowserRouter>
            <App />
        </BrowserRouter>
    </React.StrictMode>
)
